// Fetches FX rates from `fx_rates` table, caches in localStorage for 24h.
// Falls back to hardcoded rates if fetch fails.
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const CACHE_KEY = "bp.fx_rates";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const FALLBACK_RATES: Record<string, number> = {
  EUR: 1,
  USD: 1.08,
  NGN: 1700,
  XOF: 655,
  KES: 145,
  BRL: 5.9,
  XAF: 655,
  GNF: 9800,
  GHS: 14,
  MAD: 10.85,
  MXN: 22,
  ARS: 1100,
  EGP: 53,
  ZAR: 20,
  COP: 4500,
  IDR: 17000,
  PHP: 62,
  VND: 27000,
  INR: 90,
};

interface CachedRates {
  rates: Record<string, number>;
  fetchedAt: number;
}

function readCache(): Record<string, number> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedRates;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return parsed.rates;
  } catch {
    return null;
  }
}

function writeCache(rates: Record<string, number>) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ rates, fetchedAt: Date.now() } satisfies CachedRates),
    );
  } catch {
    /* ignore quota errors */
  }
}

let inflight: Promise<Record<string, number>> | null = null;

async function fetchRates(): Promise<Record<string, number>> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { data, error } = await supabase.from("fx_rates").select("currency, rate_vs_eur");
      if (error || !data?.length) return FALLBACK_RATES;
      const out: Record<string, number> = {};
      for (const row of data) out[row.currency] = Number(row.rate_vs_eur);
      writeCache(out);
      return out;
    } catch {
      return FALLBACK_RATES;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function useFxRates(): Record<string, number> {
  const [rates, setRates] = useState<Record<string, number>>(() => readCache() ?? FALLBACK_RATES);

  useEffect(() => {
    const cached = readCache();
    if (cached) {
      setRates(cached);
      return;
    }
    let cancelled = false;
    fetchRates().then((r) => {
      if (!cancelled) setRates(r);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return rates;
}
